/**
 * Shared vocabulary for the email-notice surfaces (#1911): status → chip tone
 * and i18n key, plus the gate-two rule.
 *
 * Kept out of the components so the list page and the drawer cannot label the
 * same status two different ways — a notice reading 「已发送」in the table and
 * 「草稿」in the drawer would look like two notices.
 */
import type {
  BrandId,
  EmailNoticeRecipientStatus,
  EmailNoticeStatus,
  NoticeAudienceType,
} from '@nadoutong/contracts';
import { BULK_EMAIL_BRAND } from '@nadoutong/contracts';
import type { ChipTone } from '../../ui/index.ts';
import type { I18nKey } from '../../i18n/index.ts';

/** Tab order = lifecycle order, so the table reads left-to-right as time. */
export const NOTICE_STATUS_ORDER: readonly EmailNoticeStatus[] = ['draft', 'sent', 'cancelled'];

const STATUS_TONES: Readonly<Record<EmailNoticeStatus, ChipTone>> = {
  draft: 'neutral',
  sent: 'ok',
  cancelled: 'neutral',
};

const STATUS_LABELS: Readonly<Record<EmailNoticeStatus, I18nKey>> = {
  draft: 'email_notices.status.draft',
  sent: 'email_notices.status.sent',
  cancelled: 'email_notices.status.cancelled',
};

export function noticeStatusTone(status: EmailNoticeStatus): ChipTone {
  return STATUS_TONES[status];
}

export function noticeStatusLabel(status: EmailNoticeStatus): I18nKey {
  return STATUS_LABELS[status];
}

/**
 * One label per audience strategy.
 *
 * A `Record` over the union rather than a lookup with a fallback: adding a
 * second strategy to `NoticeAudience` must fail to compile here, because a
 * strategy the operator cannot NAME is one they cannot choose between.
 */
const AUDIENCE_LABELS: Readonly<Record<NoticeAudienceType, I18nKey>> = {
  models_used: 'email_notices.audience.models_used',
};

export function audienceLabel(type: NoticeAudienceType): I18nKey {
  return AUDIENCE_LABELS[type];
}

const RECIPIENT_TONES: Readonly<Record<EmailNoticeRecipientStatus, ChipTone>> = {
  pending: 'neutral',
  sent: 'ok',
  failed: 'bad',
};

const RECIPIENT_LABELS: Readonly<Record<EmailNoticeRecipientStatus, I18nKey>> = {
  pending: 'email_notices.recipient_status.pending',
  sent: 'email_notices.recipient_status.sent',
  failed: 'email_notices.recipient_status.failed',
};

export function recipientStatusTone(status: EmailNoticeRecipientStatus): ChipTone {
  return RECIPIENT_TONES[status];
}

export function recipientStatusLabel(status: EmailNoticeRecipientStatus): I18nKey {
  return RECIPIENT_LABELS[status];
}

/**
 * Where gate two stands for a notice.
 *
 * ── ONE CHANNEL, SO ONE REHEARSAL (#1935) ───────────────────────────────────
 * Gate two used to be per brand: each brand was a separate SMTP account behind
 * a separate verified sender domain, each separately cold, so proving one said
 * nothing about the other. Every bulk mail now leaves as `BULK_EMAIL_BRAND`, so
 * a single rehearsal exercises the sender the blast will use and the gate is a
 * two-state verdict again.
 *
 * A rehearsal that fails writes no proof row — it comes back 502
 * `EMAIL_NOTICE_TEST_SEND_FAILED` — so it is NOT a transition here: the verdict
 * stays whatever it was, and `none` keeps meaning "no rehearsal has succeeded"
 * rather than "none was attempted".
 *
 * This is a COMPARISON, not a field, and both sides of it ride on
 * `EmailNoticeDetail`. `notice.test_sent_at` is deliberately NOT the verdict:
 * it and the proof rows are cleared by the same accepted edit so they agree
 * today, but the rows are the evidence and the timestamp is a summary. It does
 * carry one job of its own — it is the backstop for a notice whose frozen list
 * is EMPTY, where `/send` refuses on `test_sent_at === null` after the
 * anti-join finds nothing to complain about.
 */
export type SendGateTwoState =
  | { readonly kind: 'passed' }
  /** No successful rehearsal — no proof row, and the backend agrees. */
  | { readonly kind: 'none' };

export interface GateTwoInput {
  readonly testSentAt: string | null;
  /** `EmailNoticeDetail.proven_brands` — the raw proof set, always present. */
  readonly provenBrands: readonly BrandId[];
}

export function evaluateGateTwo(input: GateTwoInput): SendGateTwoState {
  // BOTH conditions, not either. `test_sent_at` alone would pass a notice whose
  // proof rows were cleared without it; the proof row alone would pass the
  // empty-frozen-list case that `/send` refuses on the timestamp. Requiring
  // both keeps this console's lamp and the server's refusal on the same side of
  // every state either one can distinguish.
  if (input.testSentAt === null) return { kind: 'none' };
  return input.provenBrands.includes(BULK_EMAIL_BRAND) ? { kind: 'passed' } : { kind: 'none' };
}
